import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  Sparkles, Video, Image as ImageIcon, Zap, Crown, Gauge, Server, Cpu,
  ArrowRight, Search, Check, X, Star,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import ModelCover from '../components/ModelCover';
import AnimatedNumber from '../components/AnimatedNumber';
import CinematicBackdrop from '../cinematic/CinematicBackdrop';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  premium:  { label: 'PREMIUM',  color: '#FFD93D', icon: Crown,    desc: 'Best-in-class quality' },
  standard: { label: 'STANDARD', color: '#FF6B4A', icon: Sparkles, desc: 'Balanced performance' },
  fast:     { label: 'FAST',     color: '#34B76F', icon: Gauge,    desc: 'Speed-optimized' },
};

const PROVIDER_META = {
  server: { label: 'Cloud',  color: '#4A7CFF', icon: Server, desc: 'Always available' },
  worker: { label: 'Worker', color: '#A855F7', icon: Cpu,    desc: 'Runs on GPU network' },
};

const FILTERS = [
  { id: 'all',     label: 'All',     icon: Sparkles  },
  { id: 'image',   label: 'Image',   icon: ImageIcon },
  { id: 'video',   label: 'Video',   icon: Video     },
  { id: 'premium', label: 'Premium', icon: Crown     },
  { id: 'fast',    label: 'Fast',    icon: Zap       },
  { id: 'instant', label: 'Cloud',   icon: Server    },
];

export default function ModelsPage() {
  const { user } = useAuth();
  const [models, setModels] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [activeModel, setActiveModel] = useState(null);

  useEffect(() => {
    axios.get(`${API}/models`).then((r) => setModels(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Load user's recent models (auth optional — returns empty if unauthed)
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    // Auth via httpOnly session cookie — withCredentials sends it automatically.
    axios.get(`${API}/models/recent`, { withCredentials: true })
      .then((r) => { if (!cancelled) setRecent(r.data?.recent || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Hide recents when logged out (derived, not stateful)
  const showRecent = !!user && recent.length > 0;

  const filtered = useMemo(() => {
    return models.filter((m) => {
      if (filter === 'image'    && m.kind !== 'image')        return false;
      if (filter === 'video'    && m.kind !== 'video')        return false;
      if (filter === 'premium'  && m.tier !== 'premium')      return false;
      if (filter === 'fast'     && m.tier !== 'fast')         return false;
      if (filter === 'instant'  && m.provider !== 'server')   return false;
      if (query) {
        const q = query.toLowerCase();
        if (!m.name.toLowerCase().includes(q) && !m.desc.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [models, filter, query]);

  const stats = useMemo(() => ({
    total:   models.length,
    image:   models.filter((m) => m.kind === 'image').length,
    video:   models.filter((m) => m.kind === 'video').length,
    premium: models.filter((m) => m.tier === 'premium').length,
    instant: models.filter((m) => m.provider === 'server').length,
  }), [models]);

  return (
    <div className="min-h-screen text-white relative cinematic-grain" data-testid="models-page">
      <CinematicBackdrop intensity="soft" />
      <Navbar />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-20 pb-24 relative z-10">

        {/* ── HEADER ── */}
        <div className="relative text-center mb-12">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FFD93D] mb-4">The Studio</p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-[100px] font-black tracking-[-0.03em] uppercase text-white mb-6 leading-[0.95]" data-testid="models-heading">
            <span className="block">{stats.total} MODELS.</span>
            <span className="block italic bg-gradient-to-r from-[#FF6B4A] via-[#E040A0] to-[#FFD93D] bg-clip-text text-transparent">ONE STUDIO.</span>
          </h1>
          <p className="text-base lg:text-lg text-white/55 max-w-2xl mx-auto leading-relaxed mb-6">
            Every other platform locks you to ONE engine. DIS2ORT gives you the entire AI image &amp; video toolbox in a single tab —
            <span className="text-white"> Midjourney for cinematic, FLUX for portraits, Sora for video, Ideogram for text rendering.</span>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs font-medium" data-testid="value-pills">
            {[
              { num: `$${(stats.total * 12).toFixed(0)}+`, label: 'monthly subscriptions replaced' },
              { num: '1', label: 'login. One bill. Zero hassle.' },
              { num: '50 free', label: 'credits to start — no card' },
            ].map((p) => (
              <div key={p.label} className="px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08]">
                <span className="font-bold text-[#FFD93D]">{p.num}</span>
                <span className="text-white/60 ml-1.5">{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── STATS STRIP ── */}
        <div className="relative grid grid-cols-2 md:grid-cols-5 gap-2 mb-10" data-testid="stats-strip">
          {[
            { k: 'TOTAL MODELS',    v: stats.total,   accent: '#FF6B4A' },
            { k: 'IMAGE',           v: stats.image,   accent: '#4A7CFF' },
            { k: 'VIDEO',           v: stats.video,   accent: '#E040A0' },
            { k: 'PREMIUM-TIER',    v: stats.premium, accent: '#FFD93D' },
            { k: 'INSTANT (CLOUD)', v: stats.instant, accent: '#34B76F' },
          ].map((s) => (
            <div key={s.k} className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] text-center hover:border-white/20 transition-colors">
              <div className="font-display text-4xl font-black tracking-tight" style={{ color: s.accent }}>
                <AnimatedNumber value={s.v} duration={1400} testid={`stat-${s.k.toLowerCase().replace(/\s+/g, '-')}`} />
              </div>
              <div className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mt-1">{s.k}</div>
            </div>
          ))}
        </div>

        {/* ── MY RECENT MODELS (logged-in users only) ── */}
        {showRecent && (
          <div className="relative mb-10" data-testid="recent-models-rail">
            <div className="flex items-end justify-between mb-3">
              <div>
                <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FFD93D] mb-1">For you</p>
                <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white">Jump back into your last 5 models</h2>
              </div>
              <span className="hidden sm:block text-[10px] font-bold tracking-[0.2em] uppercase text-white/40">Personalized · live history</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {recent.map((m) => (
                <button key={m.id} onClick={() => setActiveModel(m)}
                  data-testid={`recent-model-${m.id}`}
                  className="group relative aspect-[5/6] rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-[#FFD93D]/40 transition-colors text-left">
                  <ModelCover model={m} size={300} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                  <div className="absolute top-2 left-2">
                    <span className="px-1.5 py-0.5 rounded-full bg-black/70 backdrop-blur text-[8px] font-bold tracking-[0.2em] uppercase text-[#FFD93D]">Recent</span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-3">
                    <h3 className="font-display text-base font-black tracking-tight text-white leading-tight mb-0.5">{m.name}</h3>
                    <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-white/50">{m.kind} · {m.speed}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── FILTERS + SEARCH ── */}
        <div className="relative flex flex-col lg:flex-row gap-3 items-center justify-between mb-8" data-testid="filter-bar">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = filter === f.id;
              return (
                <button key={f.id} onClick={() => setFilter(f.id)}
                  data-testid={`filter-${f.id}`}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold tracking-wider uppercase transition-colors ${active ? 'bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white shadow-[0_4px_16px_rgba(255,107,74,0.3)]' : 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08] border border-white/[0.06]'}`}>
                  <Icon className="w-3 h-3" />
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              data-testid="search-models"
              placeholder="Search models, e.g. anime, portrait, video..."
              className="w-full pl-9 pr-3 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-full text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF6B4A]/40 transition-colors" />
          </div>
        </div>

        {/* ── GRID ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="models-loading">
            {Array.from({ length: 8 }).map((_, i) => <div key={`skel-${i}`} className="aspect-[5/6] rounded-2xl bg-white/[0.03] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-white/50" data-testid="models-empty">No models match those filters.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="models-grid">
            {filtered.map((m) => <ModelCard key={m.id} model={m} onOpen={() => setActiveModel(m)} />)}
          </div>
        )}

        {/* ── VALUE COMPARISON ── */}
        <div className="relative mt-20" data-testid="value-comparison">
          <div className="text-center mb-8">
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF6B4A] mb-3">Why it&apos;s worth it</p>
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight uppercase text-white mb-2">Stop paying 6 subscriptions.</h2>
            <p className="text-sm text-white/55 max-w-md mx-auto">A single DIS2ORT credit pack costs less than ONE month of Midjourney — and unlocks 150 models.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            <ComparisonCard title="Buying each separately" total="$130+/mo" items={[
              { name: 'Midjourney Standard', price: '$30/mo' },
              { name: 'Runway Gen-3',         price: '$28/mo' },
              { name: 'Leonardo Pro',         price: '$24/mo' },
              { name: 'Ideogram Plus',        price: '$20/mo' },
              { name: 'Pika Pro',             price: '$18/mo' },
              { name: 'Luma Dream Machine',   price: '$10/mo' },
            ]} bad />
            <ComparisonCard title="DIS2ORT Pro" total="$9.99 once" items={[
              { name: 'All 150 models above', price: 'Included' },
              { name: 'DALL·E 3, Imagen 3, Recraft', price: 'Included' },
              { name: 'Sora 2, Kling 2.0, Hailuo',   price: 'Included' },
              { name: 'FLUX, SDXL, SD 3.5',          price: 'Included' },
              { name: 'Image → Video upgrade',       price: 'Included' },
              { name: 'Credits never expire',        price: 'Forever' },
            ]} />
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing">
              <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold uppercase tracking-[0.15em] px-7 py-5 text-xs rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_8px_24px_rgba(255,107,74,0.4)] transition-shadow" data-testid="pricing-cta">
                See Pricing <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* ── FINAL CTA ── */}
        <div className="relative mt-20 p-8 lg:p-12 rounded-3xl bg-gradient-to-br from-[#FF6B4A]/15 via-[#E040A0]/10 to-[#FFD93D]/15 border border-white/[0.08] text-center" data-testid="final-cta">
          <h3 className="font-display text-3xl sm:text-5xl font-black tracking-tight uppercase text-white mb-4">
            Stop choosing.<br /><span className="italic bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] bg-clip-text text-transparent">Start creating.</span>
          </h3>
          <p className="text-sm text-white/60 mb-6 max-w-md mx-auto">150 models. 42 styles. One studio. Image to video. 50 free credits to try everything.</p>
          <Link to={user ? '/generate' : '/login'}>
            <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold uppercase tracking-[0.15em] px-10 py-6 text-sm rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_12px_40px_rgba(255,107,74,0.4)] transition-shadow" data-testid="final-cta-btn">
              Start Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* ── DETAIL MODAL ── */}
      {activeModel && <ModelModal model={activeModel} user={user} onClose={() => setActiveModel(null)} />}
    </div>
  );
}

function ModelCard({ model, onOpen }) {
  const tier = TIER_META[model.tier] || TIER_META.standard;
  const prov = PROVIDER_META[model.provider] || PROVIDER_META.server;
  const TierIcon = tier.icon;
  const ProvIcon = prov.icon;
  const cardRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [burst, setBurst] = useState(false);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rX = useSpring(useTransform(my, [0, 1], [6, -6]), { damping: 22, stiffness: 200 });
  const rY = useSpring(useTransform(mx, [0, 1], [-8, 8]), { damping: 22, stiffness: 200 });

  const handleMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const handleLeave = () => { mx.set(0.5); my.set(0.5); setHover(false); };
  const handleClick = () => {
    setBurst(true);
    setTimeout(() => setBurst(false), 700);
    setTimeout(onOpen, 120);
  };

  const shards = useMemo(() => Array.from({ length: 16 }, (_, i) => {
    const a = (i / 16) * Math.PI * 2;
    return {
      dx: Math.cos(a) * (50 + Math.random() * 60),
      dy: Math.sin(a) * (50 + Math.random() * 60),
      rot: (Math.random() - 0.5) * 160,
      delay: Math.random() * 0.08,
      color: ['#FF6B4A', '#E040A0', '#A3FF00', '#FFD93D'][i % 4],
    };
  }), []);

  return (
    <motion.button
      ref={cardRef}
      onClick={handleClick}
      onMouseMove={handleMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={handleLeave}
      style={{ rotateX: rX, rotateY: rY, transformStyle: 'preserve-3d', transformPerspective: 1200 }}
      whileHover={{ y: -8 }}
      transition={{ type: 'spring', damping: 22, stiffness: 240 }}
      data-testid={`model-card-${model.id}`}
      className="group relative rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.25] transition-colors text-left aspect-[5/6] cursor-pointer">
      {/* Animated conic-gradient border on hover */}
      <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none -z-10"
        style={{
          background: 'conic-gradient(from var(--ang, 0deg), #FF6B4A, #E040A0, #A3FF00, #4A7CFF, #FF6B4A)',
          filter: 'blur(10px)',
          animation: hover ? 'border-spin 3s linear infinite' : 'none',
        }}
      />

      {/* Cover with scale on hover */}
      <motion.div animate={{ scale: hover ? 1.07 : 1 }} transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0">
        <ModelCover model={model} size={420} />
      </motion.div>

      {/* Sheen sweep */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)',
          transform: hover ? 'translateX(120%) translateY(120%)' : 'translateX(-120%) translateY(-120%)',
          transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />

      {/* Distort scan-lines flash on hover */}
      {hover && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.4, 0.05, 0.3, 0.05] }}
          transition={{ duration: 0.65, times: [0, 0.2, 0.4, 0.6, 1] }}
          className="absolute inset-0 mix-blend-overlay pointer-events-none"
          style={{ background: 'repeating-linear-gradient(0deg, rgba(255,107,74,0.45) 0px, transparent 2px, transparent 4px)' }}
        />
      )}

      {/* Dark vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {/* Top badges */}
      <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
        <span className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[9px] font-bold tracking-[0.2em] uppercase flex items-center gap-1" style={{ color: tier.color }}>
          <TierIcon className="w-2.5 h-2.5" />
          {tier.label}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[9px] font-bold tracking-[0.2em] uppercase text-white/70 flex items-center gap-1">
          {model.kind === 'image' ? <ImageIcon className="w-2.5 h-2.5" /> : <Video className="w-2.5 h-2.5" />}
          {model.kind}
        </span>
      </div>
      {/* Body */}
      <div className="absolute bottom-0 inset-x-0 p-4">
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1 flex items-center gap-1.5" style={{ color: prov.color }}>
          <ProvIcon className="w-2.5 h-2.5" /> {prov.label} · {model.speed}
        </div>
        <h3 className="font-display text-xl lg:text-2xl font-black tracking-tight text-white mb-2 leading-tight">{model.name}</h3>
        <p className="text-[11px] text-white/70 leading-snug line-clamp-2 mb-3">{model.desc}</p>
        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
          <motion.span animate={{ x: hover ? 4 : 0 }} className="text-[#FF6B4A]">
            Click to try →
          </motion.span>
          <span className="text-white/60 group-hover:text-white">{model.max_n} per gen</span>
        </div>
      </div>

      {/* Burst shards on click */}
      {burst && (
        <div className="absolute inset-0 pointer-events-none z-30" aria-hidden>
          {shards.map((sh, i) => (
            <motion.span key={`shard-${i}`}
              initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
              animate={{ x: sh.dx, y: sh.dy, rotate: sh.rot, opacity: 0, scale: 0.3 }}
              transition={{ duration: 0.65, delay: sh.delay, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-sm"
              style={{ background: sh.color, boxShadow: `0 0 12px ${sh.color}` }}
            />
          ))}
        </div>
      )}
    </motion.button>
  );
}

function ModelModal({ model, user, onClose }) {
  const tier = TIER_META[model.tier] || TIER_META.standard;
  const prov = PROVIDER_META[model.provider] || PROVIDER_META.server;
  const isVideo = model.kind === 'video';
  const studioHref = `/generate?model=${encodeURIComponent(model.id)}`;
  const i2vHref = `/image-to-video?model=${encodeURIComponent(model.id)}`;

  return (
    <div className="d2-modal-backdrop fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 lg:p-8" onClick={onClose} data-testid="model-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="d2-modal-card relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/[0.10] bg-gradient-to-br from-[#1a0a1f] via-[#0A0A0F] to-[#0a0a1a] shadow-[0_30px_80px_-20px_rgba(255,107,74,0.25)]">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 hover:rotate-90 transition-transform duration-300 flex items-center justify-center" data-testid="modal-close">
          <X className="w-4 h-4 text-white" />
        </button>
        {/* Hero — procedural cover */}
        <div className="aspect-video relative overflow-hidden rounded-t-3xl">
          <ModelCover model={model} size={900} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-transparent to-transparent" />
        </div>
        {/* Body */}
        <div className="p-6 lg:p-10 -mt-12 relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: tier.color }}>{tier.label}</span>
            <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: prov.color }}>{prov.label}</span>
            <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold tracking-[0.2em] uppercase text-white/60">{model.speed}</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight text-white mb-3" data-testid="modal-name">{model.name}</h2>
          <p className="text-sm lg:text-base text-white/70 mb-6 leading-relaxed">{model.desc}</p>

          {/* Quick facts */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
            <Fact label="Type"        value={model.kind === 'image' ? 'Image' : 'Video'} accent="#FF6B4A" />
            <Fact label="Speed"       value={model.speed} accent="#34B76F" />
            <Fact label="Per gen"     value={`${model.max_n} ${model.max_n === 1 ? 'output' : 'outputs'}`} accent="#4A7CFF" />
            <Fact label="Tier"        value={tier.label} accent={tier.color} />
          </div>

          {/* Strengths */}
          <div className="mb-6">
            <p className="text-[10px] font-bold tracking-[0.2em] text-white/40 uppercase mb-2">What it&apos;s great at</p>
            <ul className="space-y-1.5">
              {strengthsFor(model).map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-white/80">
                  <Check className="w-4 h-4 text-[#34B76F] shrink-0 mt-0.5" /> {s}
                </li>
              ))}
            </ul>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Link to={user ? studioHref : '/login'} className="flex-1">
              <Button className="w-full bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold uppercase tracking-[0.15em] py-5 text-xs rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_8px_24px_rgba(255,107,74,0.3)] transition-shadow" data-testid="modal-cta-try">
                {user ? 'Try this model now' : 'Sign up to try'} <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </Link>
            {isVideo && user && (
              <Link to={i2vHref} className="flex-1">
                <Button variant="outline" className="w-full border-white/[0.15] text-white bg-white/[0.04] py-5 text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-white/[0.08] transition-colors" data-testid="modal-cta-i2v">
                  Animate an image
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value, accent }) {
  return (
    <div className="p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
      <div className="text-[9px] font-bold tracking-[0.2em] text-white/40 uppercase">{label}</div>
      <div className="text-sm font-bold mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ComparisonCard({ title, total, items, bad = false }) {
  return (
    <div className={`relative p-6 rounded-3xl border ${bad ? 'border-white/[0.06] bg-white/[0.02]' : 'border-[#FF6B4A]/30 bg-gradient-to-br from-[#FF6B4A]/10 via-[#E040A0]/5 to-transparent'}`}>
      {!bad && (
        <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white text-[9px] font-bold tracking-[0.2em] uppercase flex items-center gap-1">
          <Star className="w-2.5 h-2.5 fill-white" /> Winner
        </div>
      )}
      <p className={`text-[10px] font-bold tracking-[0.2em] uppercase mb-1 ${bad ? 'text-white/40' : 'text-[#FF6B4A]'}`}>{title}</p>
      <div className="flex items-baseline gap-2 mb-5">
        <span className={`font-display text-4xl font-black tracking-tight ${bad ? 'text-white/60 line-through decoration-2 decoration-red-500/50' : 'text-white'}`}>{total}</span>
      </div>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it.name} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              {bad ? <X className="w-3.5 h-3.5 text-red-500/70" /> : <Check className="w-3.5 h-3.5 text-[#34B76F]" />}
              <span className="text-white/80">{it.name}</span>
            </div>
            <span className={`font-bold tabular-nums ${bad ? 'text-white/50' : 'text-[#34B76F]'}`}>{it.price}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function strengthsFor(m) {
  // Hand-tuned strengths per model
  const lookup = {
    'gpt-image-1':     ['Photorealistic portraits', 'Quick concept exploration', 'Surreal compositions'],
    'dalle-3-turbo':   ['Vivid prompt adherence', 'Bold color palettes', 'Stylized illustrations'],
    'imagen-3':        ['Photorealism', 'Text rendering in image', 'Fine-grained detail'],
    'ideogram-30':     ['Posters with readable text', 'Logos & typography', 'Comic panels'],
    'recraft-v3':      ['Brand-consistent design', 'Vector-style illustrations', 'Product mockups'],
    'midjourney-v7':   ['Cinematic moods', 'Painterly textures', 'Iconic aesthetics'],
    'flux-pro':        ['Top-class face anatomy', 'Cinematic lighting', 'Photorealistic skin'],
    'flux-dev':        ['Open-source SOTA quality', 'Full creative control', 'Trainable LoRAs'],
    'flux-schnell':    ['4× faster than Dev', 'Same FLUX quality', 'Bulk variations'],
    'sdxl-turbo':      ['Stylized illustrations', 'Anime / cartoon styles', 'Fast iteration'],
    'sd-35':           ['Wide style range', 'Concept art', 'Multi-subject scenes'],
    'leonardo-phoenix':['Game asset art', 'Sharp anatomy', 'Production-grade lighting'],
    'playground-v3':   ['Painterly illustrations', 'Editorial concepts', 'Stylized portraits'],
    'stable-cascade':  ['3-stage latent quality', 'Speed/quality balance', 'Versatile subjects'],
    'auraflow-02':     ['Photorealism', 'Open-source flow matching', 'Detailed textures'],
    'sora-2':          ['Cinematic clips from text', 'Coherent physics', 'Image-to-video animation'],
    'kling-20':        ['SOTA motion physics', 'Realistic human action', 'Complex camera moves'],
    'luma-dream':      ['Buttery-smooth motion', 'Cinematic camera arcs', 'Image-to-video'],
    'hailuo-01':       ['Lowest cost per clip', 'Solid for shorts', 'Image-to-video supported'],
    'cogvideox-5b':    ['Open-source SOTA', '49-frame outputs', 'Trainable on custom data'],
    'svd-xt':          ['Animates any still', '25 frames of motion', 'Image-to-video specialist'],
    'runway-gen3':     ['Cinematic motion', 'Perfect physics', 'Director-grade controls'],
    'mochi-1':         ['Strong prompt adherence', 'Genmo open weights', 'Stylized motion'],
    'hunyuan-video':   ['Massive 13B params', 'Tencent open-source', 'Diverse style range'],
    'ltx-video':       ['Real-time on H100', 'Lightricks fast model', 'Quick iteration loops'],
  };
  return lookup[m.id] || [m.desc];
}
